import React, { useMemo } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { useFinanceDataContext } from '../../contexts/FinanceDataContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { formatSmartCurrency } from '../../utils/formatCurrency';
import { useTheme } from '../../contexts/ThemeContext';
import {
  calculateMonthlyIncome,
  calculateMonthlyExpenses,
  calculateSavingsRate,
  calculateRunway,
  calculateRunwayInDays,
} from '../../utils/financialCalculations';

const CHART_COLORS = [
  '#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed',
  '#0891b2', '#e11d48', '#65a30d', '#c026d3', '#ea580c',
];

const InsightsPage = () => {
  const {
    cashFlowItems,
    projectedBalances,
    budgets,
    getCurrentBalance,
    getItemsForDateRange,
  } = useFinanceDataContext();

  const { currencyCode } = useCurrency();
  const { theme } = useTheme();

  const fmt = (amount, mode = 'short') =>
    formatSmartCurrency(amount, { mode, currencyCode });

  // Monthly income/expenses from recurring items (in cents)
  const monthlyIncome = useMemo(
    () => calculateMonthlyIncome(cashFlowItems),
    [cashFlowItems]
  );
  const monthlyExpenses = useMemo(
    () => calculateMonthlyExpenses(cashFlowItems),
    [cashFlowItems]
  );

  // Convert from cents to dollars for display
  const monthlyIncDollars = monthlyIncome / 100;
  const monthlyExpDollars = monthlyExpenses / 100;
  const monthlySavingsDollars = monthlyIncDollars - monthlyExpDollars;
  const savingsRate = calculateSavingsRate(monthlyIncome, monthlyExpenses);

  const currentBalance = getCurrentBalance() * 100; // to cents for runway calc
  const runwayMonths = calculateRunway(currentBalance, monthlyExpenses);
  const runwayDays = calculateRunwayInDays(currentBalance, monthlyExpenses);

  // Spending breakdown by budget category (current month)
  const spendingBreakdown = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const occurrences = getItemsForDateRange(start, end);

    const expenses = occurrences.filter(
      (item) => item.type === 'expense' && !item.deleted
    );

    // Group by budget category
    const categoryMap = {};
    expenses.forEach((exp) => {
      const catId = exp.budgetCategoryId || 'uncategorized';
      const budget = budgets.find((b) => b.id === catId);
      const label = budget?.title || 'Uncategorized';
      const emoji = budget?.emoji || exp.emoji || '💳';
      const color = budget?.color || '#9ca3af';

      if (!categoryMap[catId]) {
        categoryMap[catId] = { name: label, emoji, color, total: 0, count: 0 };
      }
      categoryMap[catId].total += Math.abs(exp.amount);
      categoryMap[catId].count += 1;
    });

    return Object.values(categoryMap).sort((a, b) => b.total - a.total);
  }, [getItemsForDateRange, budgets]);

  const totalSpending = spendingBreakdown.reduce((s, c) => s + c.total, 0);

  // Projected balance chart data (next 90 days)
  const balanceChartData = useMemo(() => {
    if (!projectedBalances || Object.keys(projectedBalances).length === 0) return [];

    const entries = Object.entries(projectedBalances)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(0, 90);

    return entries.map(([date, balanceCents]) => ({
      date,
      label: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      balance: balanceCents / 100,
    }));
  }, [projectedBalances]);

  return (
    <div style={{ padding: '2rem', maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>
        Insights
      </h1>

      {/* Top metrics row */}
      <div style={gridStyle}>
        <MetricCard theme={theme}
          label="Monthly Income"
          value={fmt(monthlyIncDollars)}
          color="#059669"
          sub="Recurring"
        />
        <MetricCard theme={theme}
          label="Monthly Expenses"
          value={fmt(monthlyExpDollars)}
          color="#dc2626"
          sub="Recurring"
        />
        <MetricCard theme={theme}
          label="Monthly Savings"
          value={fmt(monthlySavingsDollars)}
          color={monthlySavingsDollars >= 0 ? '#059669' : '#dc2626'}
          sub={`${savingsRate >= 0 ? '+' : ''}${savingsRate.toFixed(1)}% rate`}
        />
        <MetricCard theme={theme}
          label="Runway"
          value={runwayMonths > 0 ? `${runwayMonths} mo` : '--'}
          color="#2563eb"
          sub={runwayDays > 0 ? `${runwayDays} days` : 'N/A'}
        />
      </div>

      {/* Projected Balance Chart */}
      {balanceChartData.length > 0 && (
        <Card theme={theme} title="Projected Balance" style={{ marginBottom: '1.5rem' }}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={balanceChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                interval="preserveStartEnd"
                tickCount={6}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickFormatter={(v) => fmt(v)}
                width={60}
              />
              <Tooltip
                formatter={(value) => [fmt(value, 'long'), 'Balance']}
                labelFormatter={(label) => label}
                contentStyle={{ borderRadius: 8, fontSize: 13 }}
              />
              <Area
                type="monotone"
                dataKey="balance"
                stroke="#2563eb"
                strokeWidth={2}
                fill="url(#balGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Spending Breakdown + Income vs Expenses side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Spending Breakdown */}
        <Card theme={theme} title="Spending This Month">
          {spendingBreakdown.length > 0 ? (
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
              {/* Pie chart */}
              <div style={{ width: 120, height: 120, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={spendingBreakdown}
                      dataKey="total"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={55}
                      strokeWidth={2}
                    >
                      {spendingBreakdown.map((entry, i) => (
                        <Cell
                          key={entry.name}
                          fill={entry.color || CHART_COLORS[i % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Legend */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {spendingBreakdown.slice(0, 6).map((cat, i) => (
                  <div
                    key={cat.name}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '3px 0',
                      fontSize: '0.8rem',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: cat.color || CHART_COLORS[i % CHART_COLORS.length],
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cat.emoji} {cat.name}
                      </span>
                    </div>
                    <span style={{ fontWeight: 600, color: theme.textColor, marginLeft: 8, flexShrink: 0 }}>
                      {fmt(cat.total)}
                    </span>
                  </div>
                ))}
                {spendingBreakdown.length > 6 && (
                  <div style={{ fontSize: '0.75rem', color: theme.tertiaryText, marginTop: 4 }}>
                    +{spendingBreakdown.length - 6} more
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: theme.tertiaryText, fontSize: '0.875rem' }}>
              No expenses this month yet
            </div>
          )}
        </Card>

        {/* Income vs Expenses bar comparison */}
        <Card theme={theme} title="Income vs Expenses">
          <div style={{ padding: '0.5rem 0' }}>
            {/* Income bar */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 4 }}>
                <span style={{ color: theme.secondaryText }}>Income</span>
                <span style={{ fontWeight: 600, color: '#059669' }}>{fmt(monthlyIncDollars)}</span>
              </div>
              <div style={{ height: 8, backgroundColor: theme.badgeBg, borderRadius: 4, overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(100, monthlyIncDollars > 0 ? 100 : 0)}%`,
                    backgroundColor: '#059669',
                    borderRadius: 4,
                    transition: 'width 0.3s',
                  }}
                />
              </div>
            </div>

            {/* Expenses bar */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 4 }}>
                <span style={{ color: theme.secondaryText }}>Expenses</span>
                <span style={{ fontWeight: 600, color: '#dc2626' }}>{fmt(monthlyExpDollars)}</span>
              </div>
              <div style={{ height: 8, backgroundColor: theme.badgeBg, borderRadius: 4, overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${monthlyIncDollars > 0 ? Math.min(100, (monthlyExpDollars / monthlyIncDollars) * 100) : 0}%`,
                    backgroundColor: '#dc2626',
                    borderRadius: 4,
                    transition: 'width 0.3s',
                  }}
                />
              </div>
            </div>

            {/* Net */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '0.75rem',
              borderTop: `1px solid ${theme.lightBorder}`,
              fontSize: '0.875rem',
            }}>
              <span style={{ fontWeight: 600, color: theme.textColor }}>Net</span>
              <span style={{
                fontWeight: 700,
                color: monthlySavingsDollars >= 0 ? '#059669' : '#dc2626',
              }}>
                {monthlySavingsDollars >= 0 ? '+' : ''}{fmt(monthlySavingsDollars)}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

// Metric card component
const MetricCard = ({ label, value, color, sub, theme }) => (
  <div
    style={{
      backgroundColor: theme.backgroundColor,
      border: `1px solid ${theme.borderColor}`,
      borderRadius: 12,
      padding: '1rem 1.25rem',
    }}
  >
    <div style={{ fontSize: '0.75rem', color: theme.secondaryText, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '1.25rem', fontWeight: 700, color }}>{value}</div>
    {sub && (
      <div style={{ fontSize: '0.7rem', color: theme.tertiaryText, marginTop: 2 }}>{sub}</div>
    )}
  </div>
);

// Card wrapper
const Card = ({ title, children, style = {}, theme }) => (
  <div
    style={{
      backgroundColor: theme.backgroundColor,
      border: `1px solid ${theme.borderColor}`,
      borderRadius: 12,
      padding: '1.25rem',
      ...style,
    }}
  >
    {title && (
      <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '1rem', color: theme.textColor }}>
        {title}
      </div>
    )}
    {children}
  </div>
);

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
  gap: '1rem',
  marginBottom: '1.5rem',
};

export default InsightsPage;
